import React, { useState } from "react";
import _ from "lodash";
import { months, status } from "../../utils/Helpers";
import AddInvoiceModal from "./AddInvoiceModal";
import { Link } from "react-router-dom";
import { updateInvoiceStatus } from "../../services";
import { useSelector } from "react-redux";
import { motion, AnimatePresence } from "framer-motion";
import { FileText, Calendar, Receipt } from "lucide-react";
import Pagination from "../../components/Pagination";
import { useEffect } from "react";

export default function InvoiceList({
  invoices,
  setInvoices,
  isbankPayrollGenrated,
  bankPayroll,
  forceMemberView = false,
}) {
  const userProfile = useSelector((state) => state.user.userinfo);
  const isAdmin = !forceMemberView && userProfile?.MemberRole == 2;
  const members = useSelector((state) => state.members.members);

  const [selectedInvoices, setSelectedInvoices] = useState([]);
  const [isLoadingStatus, setisLoadingStatus] = useState(false);

  // Pagination states
  const [currentPageOffset, setCurrentPageOffset] = useState(0);
  const itemsPerPage = 10;

  useEffect(() => {
    setCurrentPageOffset(0);
  }, [invoices]);

  const handleCheckboxChange = (invoice, isChecked) => {
    if (isChecked) {
      setSelectedInvoices((prevSelected) => [
        ...prevSelected,
        { InvoiceNo: invoice.InvoiceNo, Month: invoice.Month, Status: 3 },
      ]);
    } else {
      setSelectedInvoices((prevSelected) =>
        prevSelected.filter((item) => item.InvoiceNo !== invoice.InvoiceNo)
      );
    }
  };

  const handleSelectAllChange = (isChecked) => {
    if (isChecked) {
      const allInvoices = invoices.filter((invoice) => invoice.InvoiceStatus !== 3);
      setSelectedInvoices(
        allInvoices.map((invoice) => ({
          InvoiceNo: invoice.InvoiceNo,
          Month: invoice.Month,
          Status: 3,
        }))
      );
    } else {
      setSelectedInvoices([]);
    }
  };

  const isInvoiceSelected = (invoice) => {
    return selectedInvoices.some(
      (selectedInvoice) => selectedInvoice.InvoiceNo === invoice.InvoiceNo
    );
  };

  const postInvoiceStatus = async (invoice) => {
    try {
      let invoiceparms = {
        InvoiceNo: invoice.InvoiceNo,
        Month: invoice.Month,
        Status: invoice.Status,
      };
      const response = await updateInvoiceStatus(invoiceparms);
      return response.data;
    } catch (error) {
      console.error(`Error updating invoice ${invoice.InvoiceNo}:`, error);
      return null;
    }
  };

  const HandleUpdateStatus = async () => {
    setisLoadingStatus(true);
    const updatedInvoices = await Promise.all(
      selectedInvoices.map(async (invoice) => {
        const result = await postInvoiceStatus(invoice);
        if (result) {
          return {
            ...invoices.find((inv) => inv.InvoiceNo === invoice.InvoiceNo),
            InvoiceStatus: invoice.Status,
          };
        }
        return null;
      })
    );

    setInvoices((prevInvoices) =>
      prevInvoices.map(
        (invoice) =>
          updatedInvoices.find(
            (updated) => updated?.InvoiceNo === invoice.InvoiceNo
          ) || invoice
      )
    );
    setSelectedInvoices([]);
    setisLoadingStatus(false);
  };

  const gridClass = isAdmin 
    ? "lg:grid-cols-[50px_1.2fr_1fr_1.5fr_1fr_1fr_1.2fr_1fr_120px]" 
    : "lg:grid-cols-[1.2fr_1fr_1.5fr_1fr_1fr_1.2fr_1fr]";

  const displayInvoices = isAdmin 
    ? invoices 
    : invoices?.filter((inv) => inv?.InvoiceStatus === 3);

  const currentDisplayInvoices = displayInvoices?.slice(currentPageOffset, currentPageOffset + itemsPerPage);

  const handlePageChange = (offset) => {
    setCurrentPageOffset(offset);
  };

  return (
    <>
      <div className="list">
        <div className="flex items-center pt-3 justify-between h-6">
          {selectedInvoices.length > 0 && (
            <p
              onClick={HandleUpdateStatus}
              className={`text-blue-600 font-normal cursor-pointer flex items-center`}
            >
              {isLoadingStatus && (
                <svg
                  aria-hidden="true"
                  className="w-6 h-6 animate-spin text-blue-600 dark:text-white mr-2"
                  viewBox="0 0 100 101"
                  fill="none"
                  xmlns="http://www.w3.org/2000/svg"
                >
                  <path
                    d="M100 50.5908C100 78.2051 77.6142 100.591 50 100.591C22.3858 100.591 0 78.2051 0 50.5908C0 22.9766 22.3858 0.59082 50 0.59082C77.6142 0.59082 100 22.9766 100 50.5908ZM9.08144 50.5908C9.08144 73.1895 27.4013 91.5094 50 91.5094C72.5987 91.5094 90.9186 73.1895 90.9186 50.5908C90.9186 27.9921 72.5987 9.67226 50 9.67226C27.4013 9.67226 9.08144 27.9921 9.08144 50.5908Z"
                    fill="currentColor"
                  />
                </svg>
              )}
              {isLoadingStatus
                ? "Send to the members..."
                : "Send to the members"}
            </p>
          )}
          <div className="ml-auto mb-1" />
        </div>

        <div className="mt-7 px-4">
          <div className={`hidden lg:grid ${gridClass} gap-4 px-8 py-4 mb-4 text-xs font-semibold text-gray-400 dark:text-white uppercase tracking-[0.2em] border-b border-gray-100 dark:border-white/5`}>
            {isAdmin && (
              <div className="flex items-center justify-center">
                {!_.isEmpty(invoices) && invoices.some((invoice) => invoice.InvoiceStatus !== 3) && (
                  <input
                    id="select-all-checkbox"
                    type="checkbox"
                    className="w-4 h-4 text-blue-600 bg-gray-100 border-gray-300 rounded dark:focus:ring-blue-600 dark:ring-offset-gray-800 dark:bg-gray-700 dark:border-gray-600 cursor-pointer focus:ring-2 focus:ring-blue-500"
                    checked={selectedInvoices.length === invoices.filter((inv) => inv.InvoiceStatus !== 3).length}
                    onChange={(e) => handleSelectAllChange(e.target.checked)}
                  />
                )}
              </div>
            )}
            <div className="flex items-center">Invoice No</div>
            <div className="flex items-center">Date</div>
            <div className="flex items-center">Member Name</div>
            <div className="flex items-center">Salary</div>
            <div className="flex items-center">Tax</div>
            <div className="flex items-center">Payable Amt</div>
            <div className="flex items-center justify-center">Status</div>
            {isAdmin && <div className="text-right px-2">Actions</div>}
          </div>

          <div className="space-y-4">
            <AnimatePresence mode="popLayout">
              {!_.isEmpty(currentDisplayInvoices) ? (
                currentDisplayInvoices.map((invoice, index) => (
                  <motion.div
                    key={invoice.InvoiceNo}
                    initial={{ opacity: 0, y: 20 }}
                    animate={{ opacity: 1, y: 0 }}
                    exit={{ opacity: 0, scale: 0.95 }}
                    transition={{ delay: index * 0.05 }}
                    className={`group relative bg-white dark:bg-gray-900 hover:bg-gray-50 dark:hover:bg-[#111] rounded-[2rem] p-4 lg:px-8 border border-gray-100 dark:border-gray-800/50 shadow-sm hover:shadow-xl hover:shadow-blue-500/5 transition-all duration-300 flex flex-col lg:grid ${gridClass} items-center gap-4`}
                  >
                    {isAdmin && (
                      <div className="flex items-center justify-between lg:justify-center w-full lg:w-auto">
                        <span className="lg:hidden text-xs text-gray-500 uppercase font-semibold">Select</span>
                        <input
                          id={`checkbox-${invoice.InvoiceNo}`}
                          type="checkbox"
                          className="w-4.5 h-4.5 text-blue-600 bg-gray-100 border-gray-300 rounded dark:focus:ring-blue-600 dark:ring-offset-gray-800 dark:bg-gray-700 dark:border-gray-600 cursor-pointer disabled:opacity-40 focus:ring-2 focus:ring-blue-500"
                          checked={isInvoiceSelected(invoice) && invoice.InvoiceStatus !== 3}
                          onChange={(e) => handleCheckboxChange(invoice, e.target.checked)}
                          disabled={invoice.InvoiceStatus === 3}
                        />
                      </div>
                    )}
                    
                    {/* Invoice No */}
                    <div className="flex items-center justify-between lg:justify-start gap-2.5 w-full lg:w-auto">
                      <span className="lg:hidden text-xs text-gray-500 uppercase font-semibold">Invoice No</span>
                      <div className="flex items-center gap-2">
                        <div className="p-2 bg-blue-50 dark:bg-blue-500/10 rounded-xl hidden lg:flex items-center justify-center">
                          <FileText className="w-4 h-4 text-blue-600 dark:text-blue-400" />
                        </div>
                        <Link
                          target="_blank"
                          to={`https://portalbackend.gentrickstaffing.com/invoice/${invoice?.Year}/${invoice?.InvoiceNo}.pdf`}
                          className="text-[14px] font-black text-blue-600 dark:text-blue-400 hover:text-blue-700 hover:underline tracking-tight"
                        >
                          {invoice?.InvoiceNo}
                        </Link>
                      </div>
                    </div>

                    {/* Date */}
                    <div className="flex items-center justify-between lg:justify-start gap-2 w-full lg:w-auto">
                      <span className="lg:hidden text-xs text-gray-500 uppercase font-semibold">Date</span>
                      <div className="flex items-center gap-2 text-[13px] font-bold text-gray-700 dark:text-gray-300">
                        <Calendar className="w-4 h-4 text-gray-400 hidden lg:block" />
                        {months[invoice?.Month]} {invoice?.Year}
                      </div>
                    </div>

                    {/* Member Name */}
                    <div className="flex items-center justify-between lg:justify-start gap-2 w-full lg:w-auto">
                      <span className="lg:hidden text-xs text-gray-500 uppercase font-semibold">Member</span>
                      <span className="font-bold text-gray-900 dark:text-gray-100 text-[14px]">
                        {invoice?.MemberName}
                      </span>
                    </div>

                    {/* Salary */}
                    <div className="flex items-center justify-between lg:justify-start gap-2 w-full lg:w-auto">
                      <span className="lg:hidden text-[10px] text-gray-400 font-bold uppercase tracking-wider">Salary</span>
                      <span className="text-[13px] font-bold text-gray-600 dark:text-gray-400">
                        {invoice?.Salary !== undefined && invoice?.Salary !== null ? `Rs. ${invoice?.Salary}` : `--`}
                      </span>
                    </div>

                    {/* Tax */}
                    <div className="flex items-center justify-between lg:justify-start gap-2 w-full lg:w-auto">
                      <span className="lg:hidden text-[10px] text-gray-400 font-bold uppercase tracking-wider">Tax</span>
                      <span className="text-[13px] font-bold text-rose-500 dark:text-rose-400">
                        Rs. {invoice?.TaxAmount || 0}
                      </span>
                    </div>

                    {/* Payable */}
                    <div className="flex items-center justify-between lg:justify-start gap-2 w-full lg:w-auto">
                      <span className="lg:hidden text-[10px] text-gray-400 font-bold uppercase tracking-wider">Payable</span>
                      <span className="text-[14px] font-black text-emerald-600 dark:text-emerald-500">
                        Rs. {invoice?.PayableAmount !== undefined && invoice?.PayableAmount !== null ? invoice?.PayableAmount : 0}
                      </span>
                    </div>

                    {/* Status */}
                    <div className="flex items-center justify-between lg:justify-center w-full lg:w-auto">
                      <span className="lg:hidden text-xs text-gray-500 uppercase font-semibold">Status</span>
                      <span
                        className={`px-3 py-1.5 text-[11px] uppercase tracking-[0.1em] font-black rounded-xl border shadow-sm ${
                          invoice.InvoiceStatus === 1
                            ? "bg-gray-100 dark:bg-gray-800/80 text-gray-700 dark:text-gray-300 border-gray-200 dark:border-gray-700"
                            : invoice.InvoiceStatus === 2
                            ? "bg-red-50 dark:bg-red-500/10 text-red-600 border-red-200 dark:border-red-500/20"
                            : invoice.InvoiceStatus === 3
                            ? "bg-green-100 dark:bg-green-500/20 text-green-700 dark:text-green-400 border-green-300 dark:border-green-500/30"
                            : "bg-gray-50 dark:bg-gray-800 text-gray-500"
                        }`}
                      >
                        {status[invoice.InvoiceStatus]}
                      </span>
                    </div>

                    {/* Actions */}
                    {isAdmin && (
                      <div className="w-full lg:w-auto flex flex-col items-end justify-center lg:items-end">
                        <div className="w-full border-t border-gray-100 dark:border-gray-800 my-2 lg:hidden"></div>
                        {invoice.InvoiceStatus !== 3 ? (
                          <AddInvoiceModal
                            setInvoices={setInvoices}
                            invoiceNo={invoice?.InvoiceNo}
                            month={invoice?.Month}
                            Year={invoice?.Year}
                            memberName={invoice?.MemberName}
                            change={invoice?.changes}
                          />
                        ) : (
                          <span className="text-[10px] text-gray-400 uppercase tracking-widest font-bold hidden lg:block">Done</span>
                        )}
                      </div>
                    )}
                  </motion.div>
                ))
              ) : (
                <motion.div 
                  initial={{ opacity: 0, scale: 0.95 }}
                  animate={{ opacity: 1, scale: 1 }}
                  className="py-20 flex flex-col items-center justify-center bg-white/50 dark:bg-gray-900/50 rounded-[3rem] border-2 border-dashed border-gray-200 dark:border-gray-800 shadow-sm backdrop-blur-sm"
                >
                  <div className="p-6 bg-white dark:bg-[#111] rounded-full mb-6 text-gray-400 shadow-sm border border-gray-100 dark:border-gray-800">
                    <Receipt className="w-12 h-12" />
                  </div>
                  <h3 className="text-xl font-bold dark:text-white text-gray-900">No Invoices Found</h3>
                  <p className="text-gray-500 dark:text-gray-400 max-w-sm text-center mt-2 font-medium">
                    We couldn't find any invoices matching your records.
                  </p>
                </motion.div>
              )}
            </AnimatePresence>
          </div>

          {displayInvoices?.length > itemsPerPage && (
            <Pagination
              totalCount={displayInvoices.length}
              itemsPerPage={itemsPerPage}
              onPageChange={handlePageChange}
            />
          )}
        </div>
      </div>
    </>
  );
}